import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/errors/use-toast';
import { 
  RecommendationType, 
  RecommendationTrackingParams, 
  RecommendationStatus,
  SavedRecommendations 
} from '@/types/recommendation-tracking';

/**
 * Hook for tracking saved recommendations in agent messages
 * Manages the recommendations JSONB column and recommendation_types array
 * Now supports both temporary IDs and database IDs for reliability
 */
export const useRecommendationTracking = (messageId: string, databaseId?: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // CRITICAL: Only use database ID for queries, NEVER temporary IDs
  const effectiveMessageId = databaseId;

  // Mutation to mark a recommendation as saved
  const markRecommendationSaved = useMutation({
    mutationFn: async ({ 
      recommendationType, 
      savedItemId, 
      metadata = {} 
    }: Omit<RecommendationTrackingParams, 'messageId'>) => {
      
      // CRITICAL: Block execution if no database ID
      if (!effectiveMessageId) {
        console.error('[RecommendationTracking] BLOCKING: Attempted to save recommendation without database ID:', {
          messageId,
          databaseId,
          recommendationType,
          savedItemId
        });
        throw new Error('Cannot save recommendation: Message not yet saved to database');
      }

      console.log('[RecommendationTracking] Marking recommendation as saved:', {
        messageId: effectiveMessageId,
        originalMessageId: messageId,
        recommendationType,
        savedItemId
      });

      // First, get the current message data using the effective ID
      const { data: currentMessage, error: fetchError } = await supabase
        .from('agent_messages')
        .select('recommendations, recommendation_types')
        .eq('id', effectiveMessageId)
        .single();

      if (fetchError) {
        console.error('[RecommendationTracking] Error fetching current message:', fetchError);
        throw new Error(`Failed to fetch message: ${fetchError.message}`);
      }

      // Parse existing recommendations
      const existingRecommendations: SavedRecommendations = (currentMessage?.recommendations as unknown as SavedRecommendations) || {};
      const existingTypes: RecommendationType[] = (currentMessage?.recommendation_types as unknown as RecommendationType[]) || [];

      // Create unique key for this recommendation
      const recommendationKey = `${recommendationType}_${Date.now()}`;
      
      // Add new recommendation
      const updatedRecommendations = {
        ...existingRecommendations,
        [recommendationKey]: {
          id: savedItemId,
          type: recommendationType,
          savedAt: new Date().toISOString(),
          metadata
        }
      };

      // Add to types array if not already present
      const updatedTypes = existingTypes.includes(recommendationType) 
        ? existingTypes 
        : [...existingTypes, recommendationType];

      // Update the message using the effective ID
      const { error: updateError } = await supabase
        .from('agent_messages')
        .update({
          recommendations: updatedRecommendations as any, // Cast for Supabase compatibility
          recommendation_types: updatedTypes
        })
        .eq('id', effectiveMessageId);

      if (updateError) {
        console.error('[RecommendationTracking] Error updating message:', updateError);
        throw new Error(`Failed to update recommendations: ${updateError.message}`);
      }

      console.log('[RecommendationTracking] Successfully saved recommendation');
      
      return {
        recommendationKey,
        savedItemId,
        recommendationType,
        savedAt: new Date().toISOString()
      };
    },
    onSuccess: (data) => {
      // Invalidate relevant queries to refresh UI
      queryClient.invalidateQueries({ 
        queryKey: ['agent-messages', effectiveMessageId] 
      });
      
      // Also invalidate with original message ID for cache consistency
      if (effectiveMessageId !== messageId) {
        queryClient.invalidateQueries({ 
          queryKey: ['agent-messages', messageId] 
        });
      }
      
      // Invalidate thread messages queries
      queryClient.invalidateQueries({ 
        queryKey: ['agent-messages'] 
      });

      console.log('[RecommendationTracking] Recommendation tracking updated successfully');
    },
    onError: (error: Error) => {
      console.error('[RecommendationTracking] Error:', error);
      toast({
        title: "Failed to update recommendation status",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    },
  });

  /**
   * Check if a specific recommendation type has been saved for this message
   * Now optimized with bulk PostgreSQL function to prevent 406 errors
   */
  const isRecommendationSaved = async (recommendationType: RecommendationType): Promise<RecommendationStatus> => {
    try {
      // CRITICAL: Block execution if no database ID
      if (!effectiveMessageId) {
        console.log('[RecommendationTracking] Skipping recommendation check - no database ID:', {
          messageId,
          databaseId,
          recommendationType
        });
        return { isSaved: false };
      }

      // Use optimized bulk function instead of individual query with effective ID
      const { data, error } = await supabase.rpc('ai_agent_get_recommendations_bulk', {
        p_message_ids: [effectiveMessageId]
      });

      if (error || !data || data.length === 0) {
        return { isSaved: false };
      }

      const messageData: any = data[0];
      const recommendationTypes: RecommendationType[] = messageData.recommendation_types || [];
      const recommendations: SavedRecommendations = messageData.recommendations || {};

      const isSaved = recommendationTypes.includes(recommendationType);
      
      if (isSaved) {
        // Find the specific recommendation data
        const recommendationEntry = Object.values(recommendations).find(
          rec => rec.type === recommendationType
        );
        
        if (recommendationEntry) {
          return {
            isSaved: true,
            savedAt: recommendationEntry.savedAt,
            savedItemId: recommendationEntry.id,
            metadata: recommendationEntry.metadata
          };
        }
      }

      return { isSaved: false };
    } catch (error) {
      console.error('[RecommendationTracking] Error checking recommendation status:', error);
      return { isSaved: false };
    }
  };

  /**
   * Get the saved item ID for a specific recommendation type
   */
  const getSavedRecommendationId = async (recommendationType: RecommendationType): Promise<string | null> => {
    const status = await isRecommendationSaved(recommendationType);
    return status.savedItemId || null;
  };

  return {
    markRecommendationSaved: markRecommendationSaved.mutateAsync,
    isMarkingRecommendation: markRecommendationSaved.isPending,
    isRecommendationSaved,
    getSavedRecommendationId
  };
}; 